import { useId, useMemo, useState, type FormEvent } from "react";
import { Link } from "wouter";
import { useCreatePlacement, useNodes } from "../../api/queries";
import type { Placement } from "../../api/types";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import { EnvOverridesEditor, rowsToRecord, type EnvOverrideRow } from "./EnvOverridesEditor";
import "../../components/Form.css";
import "./services.css";

interface PlacePlacementModalProps {
  open: boolean;
  projectId: string;
  serviceId: string;
  serviceName: string;
  /** This service's existing placements — narrows the node picker to nodes
   * not already holding one (ADR-0025 §2: `UNIQUE(serviceId, nodeId)`), so
   * an operator never has to learn the constraint from a 409. */
  existingPlacements: Placement[];
  onClose: () => void;
}

/**
 * "Add a machine" (UX polish wave 2 decision 1; internally still a
 * placement, ADR-0025 §2) — creates one placement. A machine move is always
 * delete-then-create (see DeletePlacementDialog + this modal again), never
 * an in-place change, so there's no "move" flow here on purpose.
 */
export function PlacePlacementModal({
  open,
  projectId,
  serviceId,
  serviceName,
  existingPlacements,
  onClose,
}: PlacePlacementModalProps) {
  const nodeFieldId = useId();
  const coLocateId = useId();
  const { pushToast } = useToast();
  const mutation = useCreatePlacement(projectId, serviceId);
  const { data: nodesPage, isPending: nodesPending } = useNodes({ limit: 100 });

  const [nodeId, setNodeId] = useState("");
  const [coLocate, setCoLocate] = useState("");
  const [rows, setRows] = useState<EnvOverrideRow[]>([]);

  const placedNodeIds = useMemo(() => new Set(existingPlacements.map((p) => p.nodeId)), [existingPlacements]);
  const eligibleNodes = useMemo(
    () => (nodesPage?.items ?? []).filter((n) => n.status === "online" && !placedNodeIds.has(n.id)),
    [nodesPage, placedNodeIds],
  );

  function handleClose() {
    setNodeId("");
    setCoLocate("");
    setRows([]);
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!nodeId) return;
    mutation.mutate(
      {
        nodeId,
        envOverrides: rowsToRecord(rows),
        coLocate: coLocate.trim(),
      },
      {
        onSuccess: () => {
          pushToast({ title: `Placed "${serviceName}" on the selected machine.`, tone: "success" });
          handleClose();
        },
      },
    );
  }

  const canSubmit = nodeId.length > 0 && !mutation.isPending;

  return (
    <Modal open={open} title="Add a machine" onClose={handleClose} initialFocusId={nodeFieldId}>
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && (
          <p className="form-error" role="alert">
            {mutation.error instanceof Error ? mutation.error.message : "Couldn't add this machine."}
          </p>
        )}

        <label className="form-field" htmlFor={nodeFieldId}>
          <span>Machine</span>
          <select
            id={nodeFieldId}
            value={nodeId}
            onChange={(e) => setNodeId(e.target.value)}
            required
            disabled={nodesPending}
          >
            <option value="" disabled>
              {nodesPending ? "Loading machines…" : "Select a machine"}
            </option>
            {eligibleNodes.map((n) => (
              <option key={n.id} value={n.id}>
                {n.name}
              </option>
            ))}
          </select>
          {!nodesPending && eligibleNodes.length === 0 && (
            <span className="form-field__hint">
              No online machines are available to add — every online machine in the fleet already runs it, or none
              has enrolled yet. <Link href="/hosts">Provision or connect a host</Link> to give it somewhere to run.
            </span>
          )}
        </label>

        <label className="form-field" htmlFor={coLocateId}>
          <span>Co-locate tag (optional)</span>
          <input
            id={coLocateId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="e.g. storefront-edge"
            value={coLocate}
            onChange={(e) => setCoLocate(e.target.value)}
          />
          <span className="form-field__hint">
            Give this the same tag as another service's placement to land them together on the same machine.
          </span>
        </label>

        <EnvOverridesEditor rows={rows} onChange={setRows} disabled={mutation.isPending} />

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {mutation.isPending ? "Adding…" : "Add"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
